import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, switchMap, take, catchError, throwError, retry, filter } from 'rxjs';
import { ClerkService } from 'ngx-clerk-iliad';
import { environment } from '../../environments/environment';

// Helper function to decode JWT tokens
function decodeJwt(token: string): { header: any, payload: any } {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[JWT Decoder] Invalid token format');
      return { header: null, payload: null };
    }

    // Decode header (first part)
    const headerBase64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
    const headerJson = atob(headerBase64);
    const header = JSON.parse(headerJson);

    // Decode payload (second part)
    const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);

    return { header, payload };
  } catch (error) {
    console.error('[JWT Decoder] Error decoding token:', error);
    return { header: null, payload: null };
  }
}

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const clerkService = inject(ClerkService);

  // Only intercept requests to our API
  if (!request.url.startsWith(environment.apiUrl)) {
    console.log(`[Auth Interceptor] Skipping non-API request: ${request.url}`);
    return next(request);
  }

  console.log(`[Auth Interceptor] Intercepting API request: ${request.url}`);

  // Get the Clerk instance and add the session token to the request
  return clerkService.clerk$.pipe(
    // Wait until Clerk is initialized and a session exists
    // This avoids racing the first emission (which can be undefined) and sending unauthenticated API calls
    // Filter ensures we only proceed when both the Clerk instance and its session are present
    // Then take the first valid emission
    filter((clerk: any) => !!clerk && !!clerk.session),
    take(1),
    switchMap(clerk => {
      console.log(`[Auth Interceptor] Clerk instance available: ${!!clerk}`);
      console.log(`[Auth Interceptor] Clerk session available: ${!!clerk.session}`);

      // Get the session token
      console.log('[Auth Interceptor] Attempting to get session token');

      // Get the standard session token without template parameter
      // The backend expects a JWT that can be validated against the Clerk JWKS
      return from(
        // Use the standard getToken method without parameters
        (clerk.session.getToken()
          .catch((error: any) => {
            console.log('[Auth Interceptor] Error getting token:', error);
            console.log('[Auth Interceptor] Session no longer available in catch block');
            return Promise.resolve<string | null>(null);
          }) as Promise<string | null>)
      ).pipe(
        switchMap((token: string | null) => {
          console.log(`[Auth Interceptor] Token retrieved: ${!!token}`);

          if (!token) {
            console.log('[Auth Interceptor] No token found, proceeding without token');
            return next(request);
          }

          console.log(`[Auth Interceptor] Token: ${token.substring(0, 10)}...`);

          // Debug URL matching
          console.log('[Auth Interceptor] Current request URL:', request.url);
          console.log('[Auth Interceptor] URL includes "/projects/list":', request.url.includes('/projects/list'));
          console.log('[Auth Interceptor] URL includes "projects/list":', request.url.includes('projects/list'));

          // Check if this is the projects/list endpoint and print the full token
          if (request.url.includes('projects/list')) {
            console.log('[Auth Interceptor] Full JWT token for projects/list endpoint:');
            console.log(token);
          }

          // Decode and log the token details
          const decodedToken = decodeJwt(token);
          if (decodedToken.header && decodedToken.payload) {
            console.log('[Auth Interceptor] Token header:', decodedToken.header);
            console.log('[Auth Interceptor] Token payload:', decodedToken.payload);
            console.log('[Auth Interceptor] Token subject (sub):', decodedToken.payload.sub);
            console.log('[Auth Interceptor] Token expiration (exp):', new Date(decodedToken.payload.exp * 1000).toISOString());
            console.log('[Auth Interceptor] Token issued at (iat):', new Date(decodedToken.payload.iat * 1000).toISOString());
          } else {
            console.error('[Auth Interceptor] Failed to decode token');
          }

          console.log('[Auth Interceptor] Adding auth token to request');

          // Clone the request and add the authorization header
          const authReq = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });

          // Log the final request headers
          console.log('[Auth Interceptor] Final request headers:',
            Array.from(authReq.headers.keys()).map(key => `${key}: ${authReq.headers.get(key)}`));

          // Pass the cloned request with the token to the next handler
          return next(authReq).pipe(
            // Add retry logic for server errors (500) and auth errors (401)
            // This will retry the request once after a brief delay
            retry({
              count: 1,
              delay: (error, retryCount) => {
                if (error instanceof HttpErrorResponse) {
                  const status = error.status;
                  if (status === 500) {
                    console.log(`[Auth Interceptor] Retrying request after ${status} error (attempt ${retryCount})`);
                    console.log(`[Auth Interceptor] Error details:`, error);

                    // Wait 1 second before retrying
                    return new Observable(subscriber => {
                      setTimeout(() => {
                        // Instead of just completing, we need to emit a value
                        // to prevent EmptyError when using firstValueFrom
                        subscriber.next(true); // Emit a value (true) to signal retry
                        subscriber.complete();
                      }, 1000);
                    });
                  }
                }
                // For other errors, don't retry
                return throwError(() => error);
              }
            }),
            // Catch and log HTTP errors
            catchError((error: HttpErrorResponse) => {
              console.warn(`[Auth Interceptor] Request failed with status ${error.status}:`, error.message);
              if (error.error instanceof Object) {
                console.warn('[Auth Interceptor] Error details:', error.error);
              }
              // Re-throw the error to be handled by the application
              return throwError(() => error);
            })
          );
        })
      );
    }),
    catchError((error: any) => {
      console.warn('[Auth Interceptor] Error in auth interceptor:', error);
      // If there's an error getting the token, proceed with the original request
      return next(request);
    })
  );
};
